import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  CalendarDays,
  Check,
  Clock3,
  Copy,
  History,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Settings2,
  X,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PageContainer } from '../../../common/components/PageContainer';
import {
  AutomationCanvas,
  AddNodeMenu,
  CanvasNode,
  NodeType
} from '../../../modules/email-automation/components';
import { Connection, validateWorkflow } from '../../../modules/email-automation/components/AutomationCanvas';
import { useAutomationApi, AutomationRuleApi, ExecutionLogApi } from '../../../common/hooks/use-automation-api.hook';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { LimitBadge } from '../../../components/LimitBadge';
import { useEmailApi } from '../../../common/hooks/use-email-api.hook';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { useSettingsApi } from '../../../common/hooks/use-settings-api.hook';

const NODE_SPACING = 300;
const NODE_Y = 80;

/** How an execution's status reads in the history panel. */
const STATUS_TONE: Record<string, string> = {
  sent: 'text-success',
  failed: 'text-destructive',
  filtered: 'text-warning',
  pending: 'text-muted-foreground',
};
const STATUS_DOT: Record<string, string> = {
  sent: 'bg-success',
  failed: 'bg-destructive',
  filtered: 'bg-warning',
  pending: 'bg-muted-foreground',
};

/** A 30px square toolbar control; the toolbar has six of them. */
const ToolbarIconButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & { active?: boolean }
>(function ToolbarIconButton({ active, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'flex size-[30px] items-center justify-center text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40',
        active && 'bg-muted text-foreground',
        className,
      )}
      {...props}
    />
  );
});

export const EmailAutomationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useAutomationApi();
  const emailApi = useEmailApi();
  const settingsApi = useSettingsApi();
  const { getLimit } = useVendorPlan();

  // Monthly email quota — automations still run, but SENDS are skipped by the
  // backend once the cap is hit. Warn (don't block) so vendors can keep editing
  // rules for when the quota resets / they upgrade.
  const plansApi = usePlansApi();
  const [emailUsage, setEmailUsage] = useState<{ current: number; limit: number } | null>(null);
  useEffect(() => {
    let active = true;
    plansApi.getMyPlan()
      .then((r: any) => { if (active) setEmailUsage(r?.usage?.maxEmailsPerMonth ?? null); })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  const emailLimitReached =
    !!emailUsage && emailUsage.limit !== -1 && emailUsage.limit != null && emailUsage.current >= emailUsage.limit;

  const [automations, setAutomations] = useState<AutomationRuleApi[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTabIdx, setEditingTabIdx] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const [dragTabIdx, setDragTabIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  // History panel
  const [showHistory, setShowHistory] = useState(false);
  const [historyLogs, setHistoryLogs] = useState<ExecutionLogApi[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Delete confirm
  const [deleteConfirmIdx, setDeleteConfirmIdx] = useState<number | null>(null);

  // Test send
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({ open: false, message: '', severity: 'success' });

  // The toast dismisses itself; MUI's Snackbar used to own this timer.
  useEffect(() => {
    if (!toast.open) return;
    const timer = setTimeout(() => setToast(prev => ({ ...prev, open: false })), 3000);
    return () => clearTimeout(timer);
  }, [toast.open]);

  // Load automations from API + clean old localStorage
  useEffect(() => {
    localStorage.removeItem('ardealy.automations');
    localStorage.removeItem('ardealy.automations.nodes');
    localStorage.removeItem('ardealy.automations.connections');
    loadAutomations();

    // Flush pending saves on unmount
    return () => {
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    };
  }, []);

  const loadAutomations = async () => {
    setLoading(true);
    const rules = await api.list();
    setAutomations(rules);
    setLoading(false);
  };

  const currentAutomation = automations[activeTab] || null;
  // Filter out any invalid nodes (missing type or id)
  const rawNodes = currentAutomation?.nodes || [];
  const currentNodes: CanvasNode[] = rawNodes.filter((n: any) => n && n.id && n.type && typeof n.x === 'number');
  const currentConnections: Connection[] = (currentAutomation?.connections || []).filter((c: any) => c && c.from && c.to);

  // Uniqueness
  const hasTrigger = currentNodes.some(n => n.type === 'trigger');
  const hasAction = currentNodes.some(n => n.type === 'action');
  const disabledNodeTypes: NodeType[] = [];
  if (hasTrigger) disabledNodeTypes.push('trigger');
  if (hasAction) disabledNodeTypes.push('action');

  // Validation
  const issues = currentAutomation ? validateWorkflow(currentNodes, currentConnections) : [];
  const hasErrors = issues.some(i => i.type === 'error');

  // Config completeness check
  // Config is complete if we have at least a trigger and action node (config details have sensible defaults)
  const actionNode = currentNodes.find(n => n.type === 'action');
  const actionConfig = actionNode?.config || {};
  const needsSubject = actionConfig.useAI === false && !actionConfig.subject?.trim();
  const isConfigComplete = currentNodes.some(n => n.type === 'trigger') && !!actionNode && !needsSubject;

  // Save to API
  const saveTimeout = useRef<NodeJS.Timeout>();
  const pendingSave = useRef<Partial<AutomationRuleApi> | null>(null);

  const saveToApi = useCallback(async (updates: Partial<AutomationRuleApi>) => {
    if (!currentAutomation?._id) return;
    setSaving(true);
    await api.update(currentAutomation._id, updates);
    setSaving(false);
    pendingSave.current = null;
  }, [currentAutomation?._id]);

  const debouncedSave = useCallback((updates: Partial<AutomationRuleApi>) => {
    pendingSave.current = { ...pendingSave.current, ...updates };
    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      if (pendingSave.current) saveToApi(pendingSave.current);
    }, 800);
  }, [saveToApi]);

  // Flush pending save on unmount (page navigation)
  useEffect(() => {
    return () => {
      clearTimeout(saveTimeout.current);
      if (pendingSave.current && currentAutomation?._id) {
        // Fire-and-forget save
        api.update(currentAutomation._id, pendingSave.current);
      }
    };
  }, [currentAutomation?._id]);

  const handleNodesChange = (nodes: CanvasNode[]) => {
    if (!currentAutomation) return;
    setAutomations(prev => prev.map(a => a._id === currentAutomation._id ? { ...a, nodes } : a));
    debouncedSave({ nodes });
  };

  const handleConnectionsChange = (connections: Connection[]) => {
    if (!currentAutomation) return;
    setAutomations(prev => prev.map(a => a._id === currentAutomation._id ? { ...a, connections } : a));
    debouncedSave({ connections });
  };

  const handleCreateAutomation = async () => {
    const cap = getLimit('maxAutomationRules');
    if (cap !== -1 && cap !== undefined && automations.length >= cap) {
      setToast({
        open: true,
        message: t(
          'PLANS.CAP_REACHED.AUTOMATION_RULES',
          'You have reached your plan\'s automation limit. Upgrade to add more.',
        ),
        severity: 'warning',
      });
      return;
    }
    const result = await api.create({
      name: `${t('AUTOMATION.NEW')} ${automations.length + 1}`,
      nodes: [],
      connections: [],
      schedule: 'realtime',
      isActive: false,
    } as any);
    if (result) {
      setAutomations(prev => [...prev, result]);
      setActiveTab(automations.length);
    }
  };

  const handleDeleteRequest = (idx: number) => {
    const rule = automations[idx];
    if (!rule) return;
    const hasNodes = rule.nodes && rule.nodes.length > 0;
    if (hasNodes) {
      setDeleteConfirmIdx(idx);
    } else {
      handleDeleteConfirmed(idx);
    }
  };

  const handleDeleteConfirmed = async (idx: number) => {
    const rule = automations[idx];
    setDeleteConfirmIdx(null);
    if (rule?._id) {
      await api.remove(rule._id);
      setAutomations(prev => prev.filter((_, i) => i !== idx));
      setActiveTab(Math.max(0, activeTab >= automations.length - 1 ? automations.length - 2 : activeTab));
    }
  };

  const handleDuplicateAutomation = async () => {
    if (!currentAutomation?._id) return;
    const result = await api.duplicate(currentAutomation._id);
    if (result) {
      setAutomations(prev => [...prev, result]);
      setActiveTab(automations.length);
    }
  };

  const handleToggle = async () => {
    if (!currentAutomation?._id) return;
    const newState = !currentAutomation.isActive;

    // SMTP check + connection test before enabling
    if (newState) {
      try {
        const config = await settingsApi.findConfig();
        const smtp = config?.smtp;
        if (!smtp || !smtp.host || !smtp.user || smtp.pass === '') {
          setToast({ open: true, message: t('AUTOMATION.SMTP_NOT_CONFIGURED'), severity: 'warning' });
          setTimeout(() => navigate('/marketing/email/settings?tab=templates&tab=smtp'), 2500);
          return;
        }
        // Test connection
        const testResult = await emailApi.testSmtpConnection({
          host: smtp.host, port: smtp.port, user: smtp.user, pass: smtp.pass,
        });
        if (!testResult.connected) {
          setToast({ open: true, message: t('AUTOMATION.SMTP_CONNECTION_FAILED'), severity: 'error' });
          setTimeout(() => navigate('/marketing/email/settings?tab=templates&tab=smtp'), 2500);
          return;
        }
      } catch {
        setToast({ open: true, message: t('AUTOMATION.SMTP_NOT_CONFIGURED'), severity: 'warning' });
        return;
      }

      // Check workflow validity
      if (hasErrors || !isConfigComplete) {
        setToast({ open: true, message: t('AUTOMATION.VALIDATION.INCOMPLETE'), severity: 'error' });
        return;
      }
    }

    const result = await api.toggle(currentAutomation._id, newState);
    if (result) {
      setAutomations(prev => prev.map(a => a._id === currentAutomation._id ? { ...a, isActive: newState } : a));
      if (newState) {
        setToast({ open: true, message: t('AUTOMATION.ENABLED_SUCCESS'), severity: 'success' });
      }
    }
  };

  const handleNameChange = async (idx: number, name: string) => {
    const rule = automations[idx];
    if (!rule?._id) return;
    setAutomations(prev => prev.map((a, i) => i === idx ? { ...a, name } : a));
    await api.update(rule._id, { name });
  };

  const handleScheduleChange = async (field: string, value: any) => {
    if (!currentAutomation?._id) return;
    const updates = { [field]: value };
    setAutomations(prev => prev.map(a => a._id === currentAutomation._id ? { ...a, ...updates } : a));
    await api.update(currentAutomation._id, updates);
  };

  const getDefaultConfig = (type: NodeType) => {
    switch (type) {
      case 'trigger': return { triggerType: 'new_lead' };
      case 'condition': return { filterType: 'none' };
      case 'delay': return { delay: 2, delayUnit: 'hours' };
      case 'action': return { autoSelectProducts: true, maxProducts: 4, emailTemplate: 'modern-template', useAI: true };
      default: return {};
    }
  };

  const handleTabReorder = (fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return;
    const reordered = [...automations];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setAutomations(reordered);
    // Update active tab to follow the moved item
    if (activeTab === fromIdx) setActiveTab(toIdx);
    else if (fromIdx < activeTab && toIdx >= activeTab) setActiveTab(activeTab - 1);
    else if (fromIdx > activeTab && toIdx <= activeTab) setActiveTab(activeTab + 1);
  };

  const handleAddNode = (type: NodeType) => {
    const maxX = currentNodes.length > 0 ? Math.max(...currentNodes.map(n => n.x)) : -NODE_SPACING + 80;
    const newNode: CanvasNode = {
      id: `${type}-${Date.now()}`,
      type,
      title: type === 'trigger' ? t('PAGES.EMAIL_AUTOMATION.NODES.TRIGGER') :
        type === 'condition' ? t('AUTOMATION.CONDITION') :
        type === 'delay' ? t('PAGES.EMAIL_AUTOMATION.NODES.WAIT') :
          t('PAGES.EMAIL_AUTOMATION.NODES.SEND_EMAIL'),
      subtitle: t('PAGES.EMAIL_AUTOMATION.BUTTONS.CONFIGURE'),
      x: maxX + NODE_SPACING,
      y: NODE_Y,
      config: getDefaultConfig(type),
    };
    handleNodesChange([...currentNodes, newNode]);
  };

  // History
  const loadHistory = async () => {
    if (!currentAutomation?._id) return;
    setLoadingHistory(true);
    const logs = await api.getHistory(currentAutomation._id);
    setHistoryLogs(logs);
    setLoadingHistory(false);
  };

  useEffect(() => {
    if (showHistory && currentAutomation?._id) loadHistory();
  }, [showHistory, currentAutomation?._id]);

  // Auto-refresh history every 30s when panel is open and automation is active
  useEffect(() => {
    if (!showHistory || !currentAutomation?.isActive) return;
    const interval = setInterval(loadHistory, 30000);
    return () => clearInterval(interval);
  }, [showHistory, currentAutomation?._id, currentAutomation?.isActive]);

  // Test run
  const handleTestRun = async () => {
    if (!currentAutomation?._id || !testEmail.trim()) return;
    setTestSending(true);
    const result = await api.testRun(currentAutomation._id, testEmail.trim());
    setTestSending(false);
    setTestDialogOpen(false);
    setToast({ open: true, message: result.success ? t('AUTOMATION.TEST_SENT') : (result.message || 'Test failed'), severity: result.success ? 'success' : 'error' });
  };

  const breadcrumbs = [
    { label: t('SIDEBAR.MARKETING.TITLE') },
    { label: t('SIDEBAR.MARKETING.EMAIL_MARKETING'), href: '/marketing/email/compose' },
    { label: t('SIDEBAR.MARKETING.EMAIL_AUTOMATION') },
  ];

  if (loading) {
    return (
      <PageContainer requiresFeature="emailAutomation" breadcrumbs={breadcrumbs}>
        <div className="flex justify-center py-16">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    );
  }

  /* The chip + settings button that finish the tab bar, in both its states. */
  const tabBarEnd = (
    <>
      <div className="h-px flex-1 self-end border-b" />
      <div className="flex items-end border-b pe-2 pb-2">
        <LimitBadge
          current={automations.length}
          limit={getLimit('maxAutomationRules')}
          label={t('PLANS.LIMIT_BADGE.WORKFLOWS', 'workflows')}
          compact
        />
      </div>
      <div className="flex items-end border-b pb-2">
        <Button variant="outline" size="sm" className="h-8" onClick={() => navigate('/marketing/email/settings?tab=templates')}>
          <Settings2 className="size-3.5" />
          {t('SIDEBAR.MARKETING.EMAIL_SETTINGS')}
        </Button>
      </div>
    </>
  );

  return (
    <PageContainer requiresFeature="emailAutomation" breadcrumbs={breadcrumbs}>
      <div className="flex h-[calc(100vh-140px)] flex-col">

        {/* Email quota reached — automations keep running but sends are paused. */}
        {emailLimitReached && (
          <Alert className="mb-2 grid-cols-[1fr_auto] items-center text-warning">
            <AlertDescription className="text-warning">
              {t(
                'PLANS.EMAIL_LIMIT.AUTOMATION_WARNING',
                "You've reached your monthly email limit ({{limit}}). Automated emails are paused until it resets or you upgrade.",
                { limit: emailUsage?.limit ?? 0 },
              )}
            </AlertDescription>
            <Button variant="ghost" size="sm" className="text-warning" onClick={() => navigate('/home/billing/plan')}>
              {t('PLANS.LOCKED.UPGRADE_CTA', 'View plans')}
            </Button>
          </Alert>
        )}

        {/* Tabs bar */}
        <div className="flex shrink-0 items-end overflow-x-auto overflow-y-hidden px-0.5 pt-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {automations.length === 0 ? (
            <>
              <div className="flex items-end border-b pb-px">
                <Button variant="ghost" size="sm" className="mb-[3px]" onClick={handleCreateAutomation}>
                  <Plus className="size-4" />
                  {t('AUTOMATION.NEW_WORKFLOW')}
                </Button>
              </div>
              {tabBarEnd}
            </>
          ) : (
            <>
              {automations.map((rule, idx) => {
                const isActive = idx === activeTab;
                const isEditing = editingTabIdx === idx;
                return (
                  <div
                    key={rule._id}
                    draggable={!isEditing}
                    onDragStart={(e) => { setDragTabIdx(idx); e.dataTransfer.effectAllowed = 'move'; }}
                    onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                    onDragLeave={() => setDragOverIdx(null)}
                    onDrop={(e) => { e.preventDefault(); if (dragTabIdx !== null) handleTabReorder(dragTabIdx, idx); setDragTabIdx(null); setDragOverIdx(null); }}
                    onDragEnd={() => { setDragTabIdx(null); setDragOverIdx(null); }}
                    onClick={() => { if (!isEditing) { setActiveTab(idx); setShowHistory(false); } }}
                    onDoubleClick={() => { setEditingTabIdx(idx); setEditingName(rule.name); setTimeout(() => editInputRef.current?.focus(), 50); }}
                    className={cn(
                      'group/tab -mb-px flex max-w-[200px] min-w-0 items-center gap-1.5 px-3 py-1.5 transition-colors',
                      isEditing ? 'cursor-text' : 'cursor-pointer',
                      dragTabIdx === idx && 'opacity-40',
                      dragOverIdx === idx && dragTabIdx !== null && dragTabIdx !== idx && 'border-s-2 border-s-foreground',
                      isActive
                        ? 'border border-b-transparent bg-background'
                        : 'border border-transparent border-b-border hover:bg-accent',
                    )}
                  >
                    <span
                      className={cn(
                        'size-1.5 shrink-0 rounded-full',
                        rule.isActive ? 'bg-success' : rule.lastError ? 'bg-destructive' : 'bg-border',
                      )}
                    />
                    {isEditing ? (
                      <input
                        ref={editInputRef}
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onBlur={() => { if (editingName.trim()) handleNameChange(idx, editingName.trim()); setEditingTabIdx(null); }}
                        onKeyDown={e => { if (e.key === 'Enter') { if (editingName.trim()) handleNameChange(idx, editingName.trim()); setEditingTabIdx(null); } if (e.key === 'Escape') setEditingTabIdx(null); }}
                        onClick={e => e.stopPropagation()}
                        className="w-full min-w-[60px] border-0 bg-transparent text-[13px] font-medium outline-none"
                      />
                    ) : (
                      <span className={cn('truncate text-sm', isActive ? 'font-medium' : 'text-muted-foreground')}>
                        {rule.name}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDeleteRequest(idx); }}
                      aria-label={t('AUTOMATION.DELETE_TITLE')}
                      className={cn(
                        'flex size-4 shrink-0 items-center justify-center text-muted-foreground transition-opacity hover:text-destructive',
                        isActive ? 'opacity-60 hover:opacity-100' : 'opacity-0 group-hover/tab:opacity-100',
                      )}
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                );
              })}

              <div className="flex items-end border-b ps-0.5 pb-px">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="mb-0.5"
                      onClick={handleCreateAutomation}
                      aria-label={t('AUTOMATION.NEW_WORKFLOW')}
                    >
                      <Plus className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('AUTOMATION.NEW_WORKFLOW')}</TooltipContent>
                </Tooltip>
              </div>
              {tabBarEnd}
            </>
          )}
        </div>

        {/* Content */}
        {currentAutomation ? (
          <div className="flex flex-1 flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="mt-2 flex shrink-0 items-center gap-2 bg-background px-3 py-1.5">
              {/* Schedule toggle */}
              <div className="flex items-center overflow-hidden border">
                {(['realtime', 'daily'] as const).map(s => {
                  const isSelected = (currentAutomation.schedule || 'realtime') === s;
                  const Icon = s === 'realtime' ? Clock3 : CalendarDays;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleScheduleChange('schedule', s)}
                      aria-pressed={isSelected}
                      className={cn(
                        'flex items-center gap-1.5 px-2.5 py-1 text-xs transition-colors',
                        s === 'realtime' && 'border-e',
                        isSelected ? 'bg-muted font-medium' : 'text-muted-foreground hover:bg-accent',
                      )}
                    >
                      <Icon className="size-3.5" />
                      {t(`AUTOMATION.SCHEDULE_${s.toUpperCase()}`)}
                    </button>
                  );
                })}
              </div>

              {currentAutomation.schedule === 'daily' && (
                <select
                  value={currentAutomation.scheduleTime || '09:00'}
                  onChange={(e) => handleScheduleChange('scheduleTime', e.target.value)}
                  className="cursor-pointer border bg-background px-2.5 py-1 text-xs outline-none hover:border-foreground/40 focus:border-foreground/40"
                >
                  {Array.from({ length: 24 }, (_, i) => {
                    const h = i.toString().padStart(2, '0');
                    const label = i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`;
                    return <option key={h} value={`${h}:00`}>{label}</option>;
                  })}
                </select>
              )}

              {saving && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}

              {currentAutomation.lastError && (
                <Badge variant="outline" className="max-w-[200px] border-destructive/40 text-destructive">
                  <span className="truncate">{currentAutomation.lastError}</span>
                </Badge>
              )}

              <div className="flex-1" />

              {/* History */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToolbarIconButton
                    active={showHistory}
                    onClick={() => setShowHistory(!showHistory)}
                    className={cn(testDialogOpen && 'hidden')}
                    aria-label={t('AUTOMATION.HISTORY_TOOLTIP')}
                  >
                    <History className="size-4" />
                  </ToolbarIconButton>
                </TooltipTrigger>
                <TooltipContent>{t('AUTOMATION.HISTORY_TOOLTIP')}</TooltipContent>
              </Tooltip>

              {/* Test run — inline expandable */}
              {!testDialogOpen ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToolbarIconButton
                      onClick={() => { setTestDialogOpen(true); setTestEmail(''); }}
                      disabled={!isConfigComplete || hasErrors}
                      aria-label={t('AUTOMATION.TEST_TOOLTIP')}
                    >
                      <Play className="size-4" />
                    </ToolbarIconButton>
                  </TooltipTrigger>
                  <TooltipContent>{t('AUTOMATION.TEST_TOOLTIP')}</TooltipContent>
                </Tooltip>
              ) : (
                <div className="flex w-[280px] animate-in items-center gap-1 border bg-background py-0.5 ps-3 pe-1 fade-in slide-in-from-right-2">
                  <input
                    value={testEmail}
                    onChange={e => setTestEmail(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && testEmail.trim()) handleTestRun(); if (e.key === 'Escape') setTestDialogOpen(false); }}
                    placeholder="email@example.com"
                    autoFocus
                    className="min-w-0 flex-1 border-0 bg-transparent text-[13px] outline-none"
                  />
                  {testSending ? (
                    <Loader2 className="mx-1 size-3.5 animate-spin text-muted-foreground" />
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleTestRun}
                        disabled={!testEmail.trim()}
                        aria-label={t('AUTOMATION.TEST_TOOLTIP')}
                        className={cn(
                          'flex size-6.5 items-center justify-center transition-colors',
                          testEmail.trim() ? 'text-success hover:bg-success/10' : 'text-muted-foreground',
                        )}
                      >
                        <Play className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setTestDialogOpen(false)}
                        aria-label={t('COMMON.CANCEL')}
                        className="flex size-6.5 items-center justify-center text-muted-foreground transition-colors hover:bg-accent"
                      >
                        <X className="size-3.5" />
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Duplicate */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToolbarIconButton onClick={handleDuplicateAutomation} aria-label={t('AUTOMATION.DUPLICATE_TOOLTIP')}>
                    <Copy className="size-3.5" />
                  </ToolbarIconButton>
                </TooltipTrigger>
                <TooltipContent>{t('AUTOMATION.DUPLICATE_TOOLTIP')}</TooltipContent>
              </Tooltip>

              <div className="h-[18px] w-px bg-border" />

              {/* Active toggle */}
              <div className="flex items-center gap-2">
                <span className={cn('text-xs font-medium', currentAutomation.isActive ? 'text-success' : 'text-muted-foreground')}>
                  {currentAutomation.isActive ? t('PAGES.EMAIL_AUTOMATION.STATUS_ENABLED') : t('PAGES.EMAIL_AUTOMATION.STATUS_DISABLED')}
                </span>
                <Switch
                  checked={currentAutomation.isActive}
                  onCheckedChange={handleToggle}
                  className="data-checked:bg-success"
                />
              </div>
            </div>

            {/* Main area: Canvas + optional History panel */}
            <div className="flex flex-1 overflow-hidden">
              {/* Canvas */}
              <div className="relative mt-2 flex-1 overflow-hidden">
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* Physical `right`: the canvas is an LTR coordinate space. */}
                    <Button
                      variant="outline"
                      size="icon-sm"
                      className="absolute top-3 right-3 z-10 bg-background"
                      onClick={loadAutomations}
                      aria-label={t('AUTOMATION.REFRESH_TOOLTIP')}
                    >
                      <RefreshCw className="size-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('AUTOMATION.REFRESH_TOOLTIP')}</TooltipContent>
                </Tooltip>
                <AutomationCanvas nodes={currentNodes} connections={currentConnections} onChange={handleNodesChange} onConnectionsChange={handleConnectionsChange} isActive={currentAutomation.isActive} />
              </div>

              {/* History panel */}
              {showHistory && (
                <div className="flex w-80 shrink-0 animate-in flex-col overflow-hidden border-s bg-background fade-in slide-in-from-right-4">
                  <div className="flex items-center justify-between border-b px-3 py-2">
                    <p className="text-sm font-medium">{t('AUTOMATION.HISTORY')}</p>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon-xs" onClick={loadHistory} aria-label={t('AUTOMATION.REFRESH_TOOLTIP')}>
                        <RefreshCw className="size-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon-xs" onClick={() => setShowHistory(false)} aria-label={t('COMMON.CANCEL')}>
                        <X className="size-3.5" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto p-2">
                    {loadingHistory ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="size-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : historyLogs.length === 0 ? (
                      <p className="py-8 text-center text-xs text-muted-foreground">{t('AUTOMATION.NO_HISTORY')}</p>
                    ) : (
                      historyLogs.map(log => (
                        <div key={log._id} className="mb-1 flex gap-2 border p-2 transition-colors hover:bg-accent">
                          <span className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', STATUS_DOT[log.status] || 'bg-muted-foreground')} />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs">{log.leadEmail}</p>
                            <p className={cn('text-[11px]', STATUS_TONE[log.status] || 'text-muted-foreground')}>
                              {t(`AUTOMATION.STATUS.${log.status.toUpperCase()}`)}{log.error ? `: ${log.error}` : ''}
                            </p>
                            {log.createdAt && (
                              <p className="text-[10px] text-muted-foreground">{new Date(log.createdAt).toLocaleString()}</p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <AddNodeMenu onAddNode={handleAddNode} disabledTypes={disabledNodeTypes} />
          </div>
        ) : (
          /* Empty state */
          <div className="flex flex-1 flex-col items-center justify-center gap-3">
            <Zap className="size-12 text-muted-foreground/20" />
            <p className="font-heading text-xl font-medium tracking-tight">{t('AUTOMATION.EMPTY_TITLE')}</p>
            <p className="mb-2 max-w-md text-center text-sm text-muted-foreground">{t('AUTOMATION.EMPTY_DESC')}</p>
            <Button variant="outline" onClick={handleCreateAutomation}>
              <Plus className="size-4" />
              {t('AUTOMATION.CREATE_FIRST')}
            </Button>
          </div>
        )}
      </div>

      {/* Delete confirm */}
      <Dialog open={deleteConfirmIdx !== null} onOpenChange={(open) => !open && setDeleteConfirmIdx(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('AUTOMATION.DELETE_TITLE')}</DialogTitle>
            <DialogDescription>{t('AUTOMATION.DELETE_DESC')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmIdx(null)}>
              {t('COMMON.CANCEL')}
            </Button>
            <Button
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
              onClick={() => deleteConfirmIdx !== null && handleDeleteConfirmed(deleteConfirmIdx)}
            >
              {t('AUTOMATION.DELETE_CONFIRM')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast */}
      {toast.open && (
        <div
          role="alert"
          className="fixed inset-x-4 bottom-6 z-50 mx-auto flex w-fit max-w-[calc(100%-2rem)] animate-in items-center gap-3 rounded-xl border bg-card px-4 py-2.5 text-sm shadow-sm fade-in slide-in-from-bottom-4"
        >
          {toast.severity === 'success' ? (
            <Check className="size-4 shrink-0 text-success" />
          ) : (
            <X className={cn('size-4 shrink-0', toast.severity === 'error' ? 'text-destructive' : 'text-warning')} />
          )}
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => setToast(prev => ({ ...prev, open: false }))}
            aria-label={t('COMMON.CANCEL')}
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}
    </PageContainer>
  );
};

export default EmailAutomationPage;
